/**
 * Catalogue SKU Backfill — temporary Admin utility.
 *
 * Preview + apply the safe backfill of `catalogue_entries.sku_links`
 * without dropping to the CLI. Same guardrails as the CLI: never
 * overwrites, never guesses.
 *
 * Delete this page (and its route in App.js + the entry in Header.jsx)
 * once the backfill is complete — the backend routes live in
 * `routes/catalogue_sku_backfill.py` and can go with it.
 */
import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Search, PlayCircle, ShieldCheck, AlertTriangle } from 'lucide-react';

import { Header } from '../../components/Header';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

const API = process.env.REACT_APP_BACKEND_URL;

const BUCKET_LABEL = {
  would_update: 'Safe to link',
  skipped_already_linked: 'Already linked',
  skipped_order_missing: 'Source order missing',
  skipped_no_base_line: 'No priced base line',
  skipped_sku_mismatch: 'SKU/rate-item mismatch',
  skipped_ambiguous_sku: 'SKU matches multiple rate items',
  skipped_conflicting_history: 'Divergent historical SKUs',
  skipped_no_source_order: 'No source order id',
};

const BUCKET_TONE = {
  would_update: 'text-emerald-700 bg-emerald-50 border-emerald-200',
  skipped_already_linked: 'text-slate-500 bg-slate-50 border-slate-200',
  skipped_order_missing: 'text-amber-700 bg-amber-50 border-amber-200',
  skipped_no_base_line: 'text-amber-700 bg-amber-50 border-amber-200',
  skipped_sku_mismatch: 'text-red-700 bg-red-50 border-red-200',
  skipped_ambiguous_sku: 'text-red-700 bg-red-50 border-red-200',
  skipped_conflicting_history: 'text-amber-800 bg-amber-100 border-amber-300',
  skipped_no_source_order: 'text-slate-500 bg-slate-50 border-slate-200',
};

// Fallback layer badge — makes it obvious in the preview which
// resolution path fired (Layer 0 = modern, 1 = sku-only lookup,
// 2 = flow snapshot). Only rendered on ``would_update`` rows.
const LAYER_LABEL = {
  layer_0: 'Layer 0 · invoice basis',
  layer_1: 'Layer 1 · sku lookup',
  layer_2: 'Layer 2 · flow snapshot',
  layer_3: 'Layer 3 · legacy pricing snapshot',
};
const LAYER_TONE = {
  layer_0: 'text-slate-600 bg-slate-100 border-slate-200',
  layer_1: 'text-indigo-700 bg-indigo-50 border-indigo-200',
  layer_2: 'text-violet-700 bg-violet-50 border-violet-200',
  layer_3: 'text-fuchsia-700 bg-fuchsia-50 border-fuchsia-200',
};

export default function CatalogueSkuBackfillPage() {
  const [catalogueNumber, setCatalogueNumber] = useState('');
  const [scanning, setScanning] = useState(false);
  const [applying, setApplying] = useState(false);
  const [scanResult, setScanResult] = useState(null); // {totals, candidates, truncated}
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleScan = async () => {
    setScanning(true);
    setScanResult(null);
    try {
      const params = new URLSearchParams();
      if (catalogueNumber.trim()) params.set('catalogue_number', catalogueNumber.trim());
      params.set('limit', '500');
      const { data } = await axios.get(
        `${API}/api/admin/catalogue-sku-backfill/scan?${params.toString()}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setScanResult(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Scan failed');
    } finally {
      setScanning(false);
    }
  };

  // IDs eligible for the "Apply safe matches" button — strictly the
  // `would_update` bucket. Nothing else is ever sent to `/apply`.
  const safeIds = useMemo(() => {
    if (!scanResult?.candidates) return [];
    return scanResult.candidates
      .filter((c) => c.bucket === 'would_update')
      .map((c) => c.entry_id);
  }, [scanResult]);

  const handleApply = async () => {
    setConfirmOpen(false);
    setApplying(true);
    try {
      const { data } = await axios.post(
        `${API}/api/admin/catalogue-sku-backfill/apply`,
        { entry_ids: safeIds },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      toast.success(
        `Applied ${data.applied} / ${data.requested}. Already-linked: ${data.skipped_already_linked}. Skipped: ${data.skipped_no_longer_safe}.`
      );
      // Re-scan so the table shrinks to what remains.
      handleScan();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Apply failed');
    } finally {
      setApplying(false);
    }
  };

  const totals = scanResult?.totals || {};

  return (
    <div className="min-h-screen bg-background" data-testid="catalogue-sku-backfill-page">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Catalogue SKU Backfill</h1>
          <p className="text-sm text-slate-500 mt-1">
            Temporary utility. Previews which auto-created catalogue entries can safely
            be linked to their base Sales SKU using the order's stored invoice basis.
            Never overwrites existing links and never guesses a SKU.
          </p>
        </div>

        {/* Search + Scan */}
        <Card>
          <CardContent className="pt-6 flex gap-3 items-end">
            <div className="flex-1">
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Catalogue number contains
              </label>
              <Input
                value={catalogueNumber}
                onChange={(e) => setCatalogueNumber(e.target.value)}
                placeholder="e.g. AWE1110"
                onKeyDown={(e) => { if (e.key === 'Enter') handleScan(); }}
                data-testid="backfill-search-input"
              />
            </div>
            <Button onClick={handleScan} disabled={scanning} data-testid="backfill-scan-btn">
              {scanning ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
              Scan
            </Button>
          </CardContent>
        </Card>

        {/* Totals */}
        {scanResult && (
          <Card data-testid="backfill-totals-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Scan results</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
              {[
                ['scanned', 'Scanned'],
                ['would_update', 'Safe to link'],
                ['skipped_no_base_line', 'No base line'],
                ['skipped_order_missing', 'Order missing'],
                ['skipped_sku_mismatch', 'SKU mismatch'],
                ['skipped_ambiguous_sku', 'Ambiguous SKU'],
                ['skipped_conflicting_history', 'Conflicting history'],
                ['skipped_already_linked', 'Already linked'],
              ].map(([k, label]) => (
                <div key={k} className="rounded-lg border border-slate-200 p-3 bg-white">
                  <div className="text-xs text-slate-500">{label}</div>
                  <div className={`text-2xl font-semibold ${k === 'would_update' ? 'text-emerald-700' : 'text-slate-900'}`}>
                    {totals[k] ?? 0}
                  </div>
                </div>
              ))}
            </CardContent>
            {scanResult.truncated && (
              <CardContent className="pt-0 -mt-2 flex items-center gap-2 text-xs text-amber-700">
                <AlertTriangle className="h-3.5 w-3.5" />
                Results truncated to first 500 rows — totals still reflect the full scan.
              </CardContent>
            )}
          </Card>
        )}

        {/* Apply action */}
        {scanResult && safeIds.length > 0 && (
          <div className="flex items-center justify-between rounded-lg border border-emerald-200 bg-emerald-50 p-4">
            <div className="flex items-center gap-3">
              <ShieldCheck className="h-5 w-5 text-emerald-600" />
              <div>
                <div className="text-sm font-medium text-emerald-900">
                  {safeIds.length} safe {safeIds.length === 1 ? 'match' : 'matches'} ready to link
                </div>
                <div className="text-xs text-emerald-800">
                  Only rows in the "Safe to link" bucket will be written. Existing links stay untouched.
                </div>
              </div>
            </div>
            <Button
              onClick={() => setConfirmOpen(true)}
              disabled={applying}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="backfill-apply-btn"
            >
              {applying ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <PlayCircle className="h-4 w-4 mr-2" />}
              Apply safe matches
            </Button>
          </div>
        )}

        {/* Detail table */}
        {scanResult && scanResult.candidates?.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Candidates</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs text-slate-600 uppercase tracking-wider">
                    <tr>
                      <th className="text-left px-4 py-2">Catalogue #</th>
                      <th className="text-left px-4 py-2">Title</th>
                      <th className="text-left px-4 py-2">Company</th>
                      <th className="text-left px-4 py-2">Order</th>
                      <th className="text-left px-4 py-2">Proposed SKU</th>
                      <th className="text-left px-4 py-2">Result</th>
                      <th className="text-left px-4 py-2">Detail</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {scanResult.candidates.map((c) => (
                      <tr key={c.entry_id} data-testid={`backfill-row-${c.catalogue_number}`}>
                        <td className="px-4 py-2 font-mono text-xs">{c.catalogue_number}</td>
                        <td className="px-4 py-2 text-slate-700">{c.title || '—'}</td>
                        <td className="px-4 py-2 text-slate-500 text-xs">{c.organization_name || c.organization_id}</td>
                        <td className="px-4 py-2 font-mono text-xs">{c.order_number ? `#${c.order_number}` : '—'}</td>
                        <td className="px-4 py-2 font-mono text-xs">{c.proposed_sku || '—'}</td>
                        <td className="px-4 py-2">
                          <div className="flex flex-col gap-1">
                            <span className={`inline-block rounded-full border px-2 py-0.5 text-[11px] w-fit ${BUCKET_TONE[c.bucket] || ''}`}>
                              {BUCKET_LABEL[c.bucket] || c.bucket}
                            </span>
                            {c.bucket === 'would_update' && c.layer && (
                              <span className={`inline-block rounded-full border px-2 py-0.5 text-[10px] w-fit ${LAYER_TONE[c.layer] || 'text-slate-600 bg-slate-100 border-slate-200'}`}>
                                {LAYER_LABEL[c.layer] || c.layer}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-2 text-xs text-slate-500">{c.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {scanResult && scanResult.candidates?.length === 0 && (
          <div className="text-center text-sm text-slate-500 py-8">
            No unlinked auto-created catalogue entries match this filter.
          </div>
        )}

        <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Apply {safeIds.length} safe {safeIds.length === 1 ? 'match' : 'matches'}?</AlertDialogTitle>
              <AlertDialogDescription>
                Each catalogue entry will be linked to the exact Sales SKU its originating
                order was priced against. Only entries still without any `sku_links` will
                be written — the operation is idempotent and safe to re-run.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleApply} data-testid="backfill-confirm-apply-btn">
                Apply {safeIds.length}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </main>
    </div>
  );
}
